import { Button, Input } from '@agms/ui'
import { useEffect, useRef } from 'react'
import {
  EQUIPMENT_ASSET_STATUSES,
  EQUIPMENT_ASSET_TYPES,
} from '../../pages/merchants/merchantFormatters'
import type { MerchantEquipmentAsset } from '../../pages/merchants/merchantFormatters'

export interface EquipmentAssetPayload {
  asset_type: MerchantEquipmentAsset['asset_type']
  make: string
  model: string
  serial_number: string
  terminal_id: string
  tracking_number: string
  status: MerchantEquipmentAsset['status']
  notes: string
}

interface EquipmentAssetModalProps {
  merchantName: string
  editingAsset: MerchantEquipmentAsset | null
  assetForm: EquipmentAssetPayload
  assetError: string | null
  addPending: boolean
  updatePending: boolean
  onChange: (updater: (current: EquipmentAssetPayload) => EquipmentAssetPayload) => void
  onCancel: () => void
  onSubmit: () => void
}

export function EquipmentAssetModal({
  merchantName,
  editingAsset,
  assetForm,
  assetError,
  addPending,
  updatePending,
  onChange,
  onCancel,
  onSubmit,
}: EquipmentAssetModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const firstControl = dialogRef.current?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])')
    firstControl?.focus()

    return () => {
      previousFocus?.focus()
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div
        ref={dialogRef}
        className="w-full max-w-2xl rounded-lg bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="equipment-asset-title"
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 id="equipment-asset-title" className="text-lg font-semibold text-gray-900">{editingAsset ? 'Edit Equipment Asset' : 'Add Equipment Asset'}</h2>
          <p className="mt-1 text-sm text-gray-500">
            This is an AGMS-only asset record for {merchantName}. It does not update Fiserv.
          </p>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit()
          }}
        >
          <div className="space-y-4 px-5 py-4">
            {assetError && (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {assetError}
              </p>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Asset Type</span>
                <select
                  value={assetForm.asset_type}
                  onChange={(event) => onChange((current) => ({ ...current, asset_type: event.target.value as MerchantEquipmentAsset['asset_type'] }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {EQUIPMENT_ASSET_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Status</span>
                <select
                  value={assetForm.status}
                  onChange={(event) => onChange((current) => ({ ...current, status: event.target.value as MerchantEquipmentAsset['status'] }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {EQUIPMENT_ASSET_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Make</span>
                <Input
                  value={assetForm.make}
                  onChange={(event) => onChange((current) => ({ ...current, make: event.target.value }))}
                  placeholder="PAX, Clover, Dejavoo"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Model</span>
                <Input
                  value={assetForm.model}
                  onChange={(event) => onChange((current) => ({ ...current, model: event.target.value }))}
                  placeholder="A920, Station, QD4"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Terminal ID</span>
                <Input
                  value={assetForm.terminal_id}
                  onChange={(event) => onChange((current) => ({ ...current, terminal_id: event.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Serial Number</span>
                <Input
                  value={assetForm.serial_number}
                  onChange={(event) => onChange((current) => ({ ...current, serial_number: event.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Tracking Number</span>
                <Input
                  value={assetForm.tracking_number}
                  onChange={(event) => onChange((current) => ({ ...current, tracking_number: event.target.value }))}
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Notes</span>
              <textarea
                value={assetForm.notes}
                onChange={(event) => onChange((current) => ({ ...current, notes: event.target.value }))}
                className="min-h-[90px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="Internal equipment notes"
              />
            </label>
          </div>
          <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={addPending || updatePending}>
              {editingAsset
                ? (updatePending ? 'Saving...' : 'Save Changes')
                : (addPending ? 'Adding...' : 'Add Asset')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
